/**
 * MyLibrarySearchPanel
 * ---
 * Extracted from `src/pages/MyLibrary.tsx` to keep the page module under our
 * size budget. Renders the "Find Published" card (source-type radio, search
 * input, DOI/ISBN detection chip, and the result list with Save / Refine
 * actions). Pure presentation — all state lives on the page.
 */
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { LibrarySearchPicker } from "@/components/library/LibrarySearchPicker";
import { HelpCircle, PencilLine } from "lucide-react";
import {
  getLibraryProviderLabel,
  libraryCopy,
  type LibrarySearchResult,
} from "@/lib/library";
import type { DetectedSourceIdentifier } from "@/lib/sourceIdentifiers";
import type { LibraryCategory } from "@/lib/libraryCategories";

export type SearchKind = "book" | "journal_article" | "chapter" | "proceedings_article";

type Props = {
  searchType: SearchKind;
  setSearchType: (v: SearchKind) => void;
  searchValue: string;
  setSearchValue: (v: string) => void;
  detectedIdentifier: DetectedSourceIdentifier | null;
  onDismissDetection: () => void;
  searching: boolean;
  results: LibrarySearchResult[];
  defaultCategory: LibraryCategory | null;
  saving: boolean;
  onSaveResult: (result: LibrarySearchResult) => void;
  onRefineResult: (result: LibrarySearchResult) => void;
};

export const MyLibrarySearchPanel = ({
  searchType,
  setSearchType,
  searchValue,
  setSearchValue,
  detectedIdentifier,
  onDismissDetection,
  searching,
  results,
  defaultCategory,
  saving,
  onSaveResult,
  onRefineResult,
}: Props) => {
  return (
    <section>
      <Card className="shadow-soft">
        <CardContent className="space-y-4 p-4 sm:p-5">
          <div className="space-y-3">
            <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
              <div className="flex items-center gap-1.5">
                <h2 className="text-base font-semibold text-foreground">Find Published</h2>
                <Popover>
                  <PopoverTrigger asChild>
                    <button
                      type="button"
                      className="inline-flex h-5 w-5 items-center justify-center rounded-full text-muted-foreground transition-colors hover:bg-muted hover:text-foreground focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                      aria-label="About these search sources"
                    >
                      <HelpCircle className="h-3.5 w-3.5" />
                    </button>
                  </PopoverTrigger>
                  <PopoverContent align="start" className="w-80 space-y-2 text-xs leading-relaxed">
                    <p>
                      <span className="font-medium text-foreground">DOI</span> (Digital Object Identifier) is a permanent ID for a published work — looks like <span className="font-mono">10.1038/s41586-020-2649-2</span>.
                    </p>
                    <p>
                      <span className="font-medium text-foreground">Crossref</span> is the official DOI registry and the canonical backbone for academic article lookup.
                    </p>
                    <p>
                      <span className="font-medium text-foreground">OpenAlex</span> adds broader scholarly coverage — preprints, working papers, and venues not always indexed by Crossref.
                    </p>
                    <p>
                      <span className="font-medium text-foreground">ISBNdb</span> powers book search for the cleanest covers and metadata.
                    </p>
                  </PopoverContent>
                </Popover>
              </div>
              <RadioGroup
                value={searchType}
                onValueChange={(value) => setSearchType(value as SearchKind)}
                className="flex flex-wrap items-center gap-x-4 gap-y-2"
              >
                <div className="flex items-center gap-2">
                  <RadioGroupItem value="book" id="find-published-book" />
                  <Label htmlFor="find-published-book" className="cursor-pointer text-sm font-normal">Books</Label>
                </div>
                <div className="flex items-center gap-2">
                  <RadioGroupItem value="journal_article" id="find-published-article" />
                  <Label htmlFor="find-published-article" className="cursor-pointer text-sm font-normal">Published Articles</Label>
                </div>
                <div className="flex items-center gap-2">
                  <RadioGroupItem value="chapter" id="find-published-chapter" />
                  <Label htmlFor="find-published-chapter" className="cursor-pointer text-sm font-normal">Book Chapters</Label>
                </div>
                <div className="flex items-center gap-2">
                  <RadioGroupItem value="proceedings_article" id="find-published-proceedings" />
                  <Label htmlFor="find-published-proceedings" className="cursor-pointer text-sm font-normal">Conference Papers</Label>
                </div>
              </RadioGroup>
            </div>
            <p className="text-xs text-muted-foreground">
              {searchType === "book"
                ? "Search by title, author, or ISBN — book metadata via ISBNdb."
                : searchType === "journal_article"
                ? "Search by title, author, or DOI — academic articles via Crossref + OpenAlex."
                : searchType === "chapter"
                ? "Find chapters in edited volumes by title, author, or DOI — Crossref + OpenAlex."
                : "Find conference / proceedings papers by title, author, or DOI — Crossref + OpenAlex."}
              {defaultCategory ? (
                <>
                  {" "}New saves start with <span className="font-medium text-foreground">{defaultCategory.name}</span>.
                </>
              ) : null}
            </p>
          </div>
          <LibrarySearchPicker
            value={searchValue}
            onChange={setSearchValue}
            placeholder={
              searchType === "book"
                ? "Search by title, author, or ISBN…"
                : searchType === "chapter"
                ? "Search chapter title, author, or DOI…"
                : searchType === "proceedings_article"
                ? "Search paper title, author, or DOI…"
                : "Search by title, author, or DOI…"
            }
          />
          {detectedIdentifier ? (
            <div className="-mt-1 flex flex-wrap items-center gap-2">
              <span className="inline-flex items-center gap-1.5 rounded-full border border-primary/40 bg-primary/10 px-2.5 py-0.5 text-[11px] text-foreground">
                Detected: {detectedIdentifier.kind === "doi" ? "DOI" : "ISBN"}{" "}
                <span className="font-mono">{detectedIdentifier.value}</span>
                <button
                  type="button"
                  onClick={onDismissDetection}
                  className="ml-1 text-muted-foreground hover:text-foreground"
                  aria-label="Dismiss detection"
                >
                  ×
                </button>
              </span>
            </div>
          ) : searchType !== "book" ? (
            <p className="-mt-1 text-[11px] text-muted-foreground">
              e.g. <span className="font-mono">10.1038/s41586-020-2649-2</span> — paste a DOI for a direct lookup.
            </p>
          ) : null}
          <div className="space-y-3">
            {searching ? <p className="text-sm text-muted-foreground">Searching…</p> : null}
            {!searching && searchValue.trim() && results.length === 0 ? (
              <p className="text-sm text-muted-foreground">
                {searchType === "book"
                  ? "No matching books yet — try another title, author, or ISBN."
                  : searchType === "chapter"
                  ? "No matching chapters yet — try another title, author, or DOI."
                  : searchType === "proceedings_article"
                  ? "No matching conference papers yet — try another title, author, or DOI."
                  : "No matching articles yet — try another title, author, or DOI."}
              </p>
            ) : null}
            {results.map((result) => {
              const isScholarly =
                result.sourceType === "journal_article" ||
                result.sourceType === "chapter" ||
                result.sourceType === "proceedings_article";
              const containerOrPublisher = isScholarly
                ? result.containerTitle ?? result.publisher
                : result.publisher;
              return (
                <div key={`${result.sourceProvider}:${result.sourceId ?? result.title}`} className="flex gap-3 rounded-md border border-border bg-card p-3">
                  <div className="h-24 w-16 shrink-0 overflow-hidden rounded-sm border border-border bg-muted">
                    {result.coverUrl ? (
                      <img src={result.coverUrl} alt={result.title} className="h-full w-full object-cover" loading="lazy" />
                    ) : (
                      <div className="flex h-full items-center justify-center px-1 text-center text-[10px] text-muted-foreground">
                        {isScholarly
                          ? result.sourceType === "chapter"
                            ? "Chapter"
                            : result.sourceType === "proceedings_article"
                            ? "Paper"
                            : "Article"
                          : "No cover"}
                      </div>
                    )}
                  </div>
                  <div className="min-w-0 flex-1 space-y-2">
                    <div className="space-y-1">
                      <div className="flex flex-wrap items-start justify-between gap-2">
                        <p className="font-medium text-foreground">{result.subtitle ? `${result.title}: ${result.subtitle}` : result.title}</p>
                        {isScholarly ? (
                          <span className="shrink-0 rounded-full border border-border bg-muted px-2 py-0.5 text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
                            {getLibraryProviderLabel(result.sourceProvider)}
                          </span>
                        ) : null}
                      </div>
                      <p className="text-sm text-muted-foreground">{result.authorText || "Unknown author"}</p>
                    </div>
                    <div className="flex flex-wrap gap-2 text-xs text-muted-foreground">
                      {containerOrPublisher ? <span>{containerOrPublisher}</span> : null}
                      {result.publishedDate ? <span>· {result.publishedDate}</span> : null}
                      {result.languageCode ? <span>· {result.languageCode}</span> : null}
                    </div>
                    {isScholarly && result.doi ? (
                      <p className="font-mono text-[11px] text-muted-foreground">DOI: {result.doi}</p>
                    ) : null}
                    <div className="flex flex-wrap gap-2">
                      <Button size="sm" onClick={() => onSaveResult(result)} disabled={saving}>
                        {libraryCopy.saveAction}
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => onRefineResult(result)}>
                        <PencilLine className="h-4 w-4" /> Refine first
                      </Button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>
    </section>
  );
};
